import styled, { css, keyframes } from 'styled-components';

import {
  COLORS,
  MOBILE,
  RUNNING_HERO_GLOW,
  PAUSED_HERO_GLOW,
  ASSET_THUMB_BASE,
  ACQ,
  ANIM,
  GRADIENTS,
} from '../../theme/tokens';
import { coverImage, hoverWash } from '../../theme/mixins';
import { Text } from '../../components';

// --- Hero --------------------------------------------------------------------
export const Hero = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

/** Hero glow by state: a running campaign reads as healthy/live (green), a paused
 * (resumable) one as live-but-idle (the same glow drained to grey), and an ended one
 * as a flat dark surface (no glow). */
export type HeroGlow = 'live' | 'paused';

const HERO_GLOW_BY_STATE: Record<HeroGlow, string> = {
  live: RUNNING_HERO_GLOW,
  paused: PAUSED_HERO_GLOW,
};

/** The card wrapping the artwork + titles and the offering block. `relative` +
 * `overflow: hidden` so the pause/resume light `Sheen` is clipped to the card's
 * rounded rectangle as it sweeps across. */
export const HeroCard = styled.div<{ $glow?: HeroGlow }>`
  position: relative;
  overflow: hidden;
  display: flex;
  align-items: stretch;
  gap: 10px;
  padding: 10px;
  border-radius: 21px;
  background-color: ${ACQ.heroBg};
  background-image: ${(p) => (p.$glow ? HERO_GLOW_BY_STATE[p.$glow] : 'none')};
  background-size: 100% 100%;
  background-repeat: no-repeat;
  box-sizing: border-box;
  ${MOBILE} {
    flex-direction: column;
  }
`;

// A diagonal light band that sweeps left→right across the hero card. Mounted
// (keyed) for one pass each time the owner pauses or resumes, then it parks
// off-screen right (animation-fill `both`) so it leaves no residue. Skewed for a
// glancing-light feel; sits above the card content but ignores pointer events.
const sheenSweep = keyframes`
  from { transform: translateX(-160%) skewX(-18deg); }
  to   { transform: translateX(260%) skewX(-18deg); }
`;

export const Sheen = styled.span`
  position: absolute;
  top: -20%;
  bottom: -20%;
  left: 0;
  width: 45%;
  z-index: 1;
  pointer-events: none;
  background: ${GRADIENTS.heroSheen};
  transform: translateX(-160%) skewX(-18deg);
  animation: ${sheenSweep} ${ANIM.HERO_SHEEN_MS}ms ${ANIM.EASE} both;
  @media (prefers-reduced-motion: reduce) {
    display: none;
  }
`;

export const HeroMain = styled.div`
  flex: 1 1 0;
  min-width: 0;
  display: flex;
  align-items: center;
  gap: 10px;
  ${MOBILE} {
    /* Stacked in the mobile column — size to content, not an equal flex share. */
    flex: 0 0 auto;
  }
`;

export const HeroThumb = styled.div<{ $src: string }>`
  flex: 0 0 130px;
  width: 130px;
  height: 120px;
  border-radius: 16px;
  ${(p) => coverImage(p.$src)}
`;

export const HeroTitles = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 10px;
  min-width: 0;
`;

export const HeroTitle = styled(Text)`
  font-size: 18px;
  font-weight: 700;
  line-height: 1.2;
  color: ${COLORS.featureName};
`;

export const HeroSubtitle = styled(Text)`
  font-size: 14px;
  font-weight: 300;
  line-height: 1.35;
  color: ${COLORS.featureName};
`;

/** Dark inset holding the Ad-Spend figure (with a divider) + the offering list. */
export const HeroOffering = styled.div`
  flex: 1 1 0;
  min-width: 0;
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 20px;
  border-radius: 22px;
  background: ${COLORS.metricTileBg};
  box-sizing: border-box;
  overflow: hidden;
  ${MOBILE} {
    /* Stacked in the mobile column: size to content. With overflow:hidden the
       flex auto min-height resolves to 0, so flex:1 1 0 would collapse it. */
    flex: 0 0 auto;
  }
`;

export const AdSpend = styled.div`
  flex: 0 0 130px;
  align-self: stretch;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding-right: 10px;
  border-right: 1px solid ${COLORS.heroDivider};
  text-align: center;
`;

export const AdSpendValue = styled.div`
  font-size: 24px;
  font-weight: 700;
  line-height: 1.1;
  color: ${COLORS.text};
`;

export const AdSpendLabel = styled(Text)`
  font-size: 16px;
  font-weight: 400;
  color: ${COLORS.text};
`;

export const OfferingList = styled.div`
  flex: 1 1 0;
  min-width: 0;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  column-gap: 32px;
  row-gap: 6px;
  ${MOBILE} {
    /* Stacked under the value on mobile, where the hero is a single column. */
    flex-direction: column;
    align-items: stretch;
  }
`;

/** One label/value pair (Offering, Duration). Stacked internally; the pairs sit
 * side by side on desktop and wrap back to a column when the hero is too narrow. */
export const OfferingGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

export const OfferingLabel = styled(Text)`
  font-size: 14px;
  font-weight: 700;
  color: ${COLORS.featureName};
`;

export const OfferingValue = styled(Text)`
  font-size: 14px;
  font-weight: 300;
  color: ${COLORS.featureName};
  /* A real (API) offering headline can be long ("First Welcome Bonus: 100% Match +
     100 Free Spins"). It wraps at spaces within its group; this also breaks a rare
     unbreakable token so it can never overflow the inset's clipped (overflow:hidden)
     bounds. No effect on the short mock/operations values. */
  overflow-wrap: anywhere;
`;

// --- Controls (Pause + optional cross-link) ----------------------------------
export const HeroControls = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 12px;
  padding: 10px 0;
  ${MOBILE} {
    flex-direction: column;
    align-items: stretch;
    gap: 10px;
  }
`;

/**
 * "Promotion also in use for Acquisition" — the optional cross-link beside the
 * Pause button (operations running-promotion page). A dark padded pill
 * (same inset surface as the offering block) so it has breathing room rather
 * than reading as bare text. `margin-right: auto` pins it to the left and pushes
 * Pause to the right, so when no cross-link is present the Pause button keeps
 * its original right-aligned position. On mobile it stacks full-width under the
 * card (HeroControls switches to a stretched column), label left + icon right.
 */
export const CrossLink = styled.button`
  margin-right: auto;
  display: inline-flex;
  align-items: center;
  gap: 10px;
  padding: 20px;
  border-radius: 22px;
  background: ${COLORS.metricTileBg};
  border: 0;
  cursor: pointer;
  color: ${COLORS.featureName};
  ${MOBILE} {
    margin-right: 0;
  }
`;

export const CrossLinkLabel = styled(Text)`
  flex: 1 1 0;
  text-align: left;
  font-size: 14px;
  font-weight: 700;
  color: ${COLORS.featureName};
`;

export const PauseButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 10px 22px;
  border: none;
  border-radius: 41px;
  background: ${COLORS.pauseBg};
  cursor: pointer;
  /* Cursor-following hover glow + lit hairline ring (shared with the CTA Button). */
  ${hoverWash}
  ${MOBILE} {
    width: 100%;
  }
`;

export const PauseLabel = styled(Text)`
  font-size: 14px;
  font-weight: 700;
  line-height: 1.4;
  color: ${COLORS.pauseFg};
`;

// Shown (instead of Pause) on a PAUSED campaign/promotion — the primary filled
// CTA treatment (same solid fill + on-accent text as the shared Button's
// `primary` variant) so resuming reads as the affirmative action.
export const ResumeButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 10px 22px;
  border: 1px solid ${COLORS.ctaSolid};
  border-radius: 41px;
  background: ${COLORS.ctaSolid};
  cursor: pointer;
  /* Cursor-following hover glow + lit hairline ring (shared with the CTA Button). */
  ${hoverWash}
  ${MOBILE} {
    width: 100%;
  }
`;

export const ResumeLabel = styled(Text)`
  font-size: 14px;
  font-weight: 700;
  line-height: 1.4;
  color: ${COLORS.ctaSolidText};
`;

// Non-interactive "Ended on {date}" tag shown (instead of Pause) on an ended
// campaign/promotion detail — a muted zinc pill.
export const EndedTag = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 10px 18px;
  border-radius: 41px;
  background: ${ACQ.badgeMutedBg};
  ${MOBILE} {
    width: 100%;
    justify-content: center;
  }
`;

export const EndedLabel = styled(Text)`
  font-size: 14px;
  font-weight: 700;
  line-height: 1.4;
  color: ${ACQ.t300};
  font-variant-numeric: tabular-nums;
`;

// --- Performance (locked / "Unlocking soon") ---------------------------------
// When the promotion performance grid has no API source yet, the section keeps its
// title but frosts each KPI card under its own LockVeil. Mirrors KpiSection's own bare
// `Section` wrapper so the locked variant sits on the page identically.
export const PerfSection = styled.section`
  display: block;
`;

// Per-tile positioning context for the LockVeil: each KPI card is frosted
// INDIVIDUALLY (not the grid as one block) so its metric name still reads through the
// veil's own header, letting owners see which figures are coming. Fills its grid cell.
export const TileLock = styled.div`
  position: relative;
`;

// --- Assets ------------------------------------------------------------------
export const AssetsSection = styled.section`
  display: block;
`;

/**
 * Shared "listing" container surface — the same dark outer-card surface the
 * acquisition Campaigns table uses, minus its tabs/filter chrome. Backs both the
 * Campaign Assets grid and the Top Websites list so the detail page reads as one
 * family of listing panels.
 */
const listingSurface = css`
  background: ${ACQ.cardOuter};
  border-radius: 16px;
  box-sizing: border-box;
`;

export const AssetsPanel = styled.div`
  ${listingSurface}
  padding: 6px;
`;

/** Masonry: two balanced columns (one on mobile). Each card's height follows its
 *  banner's aspect ratio, so creatives are shown as fully as possible and pack with no
 *  fixed-row gaps. */
export const AssetGrid = styled.div`
  column-count: 2;
  column-gap: 10px;
  ${MOBILE} {
    column-count: 1;
  }
`;

export const AssetCard = styled.div`
  display: flex;
  flex-direction: column;
  border-radius: 12px;
  background: ${COLORS.surfaceCard};
  overflow: hidden;
  margin-bottom: 10px;
  /* Keep a card whole within its masonry column. */
  -webkit-column-break-inside: avoid;
  break-inside: avoid;
`;

export const AssetThumb = styled.div<{ $from: string; $clickable?: boolean }>`
  position: relative;
  width: 100%;
  min-height: 72px;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  /* Button reset (the thumb renders as a <button> when its banner is zoomable). */
  margin: 0;
  padding: 0;
  border: none;
  font: inherit;
  color: inherit;
  text-align: inherit;
  appearance: none;
  cursor: ${(p) => (p.$clickable ? 'pointer' : 'default')};
  background-image: ${(p) => `linear-gradient(166.52deg, ${p.$from} 0%, ${ASSET_THUMB_BASE} 71.43%)`};
`;

/** The real generated banner: fills the card width at its natural aspect ratio, so it
 *  is shown as fully as possible. Very tall formats (e.g. a 160×600 skyscraper) are
 *  capped and centered, the tone gradient showing in the margin. */
export const AssetImg = styled.img`
  width: 100%;
  height: auto;
  max-height: 400px;
  object-fit: contain;
  display: block;
`;

/** The zoomed banner inside the click-to-open modal — shown at its native size, capped
 *  to the viewport (no upscaling, so raster creatives stay crisp). */
export const ZoomImg = styled.img`
  display: block;
  width: auto;
  height: auto;
  max-width: 100%;
  max-height: 72vh;
  margin: 0 auto;
  border-radius: 8px;
`;

/** Dimension / format caption under the zoomed banner. */
export const ZoomMeta = styled(Text)`
  display: block;
  margin-top: 12px;
  text-align: center;
  font-size: 12px;
  font-weight: 300;
  color: ${COLORS.textSubtle};
`;

export const DimBadge = styled(Text)`
  position: absolute;
  right: 10px;
  bottom: 8px;
  font-size: 9px;
  font-weight: 500;
  color: ${COLORS.text};
  opacity: 0.3;
`;

export const AssetInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 10px 12px 14px;
`;

export const AssetName = styled(Text)`
  font-size: 12px;
  font-weight: 500;
  color: ${COLORS.textPale};
`;

export const AssetMetaLine = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: 4px;
  font-size: 10px;
`;

export const AssetMetaType = styled(Text)`
  font-weight: 300;
  color: ${COLORS.textSubtle};
`;

export const AssetMetaChannels = styled(Text)<{ $color: string }>`
  font-weight: 600;
  color: ${(p) => p.$color};
`;

// --- Asset wireframe (schematic ad-format preview) ---------------------------
export type WireVariant = 'wide' | 'tall' | 'narrow';

export const WireBox = styled.div<{ $w: number; $h: number; $border: string; $fill: string; $variant: WireVariant }>`
  width: ${(p) => p.$w}px;
  height: ${(p) => p.$h}px;
  box-sizing: border-box;
  border-radius: 3px;
  border: 1px dashed ${(p) => p.$border};
  background: ${(p) => p.$fill};
  padding: 3px;
  display: flex;
  flex-direction: row;
  gap: 3px;
  overflow: hidden;
  align-items: ${(p) => (p.$variant === 'narrow' ? 'center' : 'stretch')};
  justify-content: ${(p) => (p.$variant === 'narrow' ? 'center' : 'flex-start')};
`;

export const WireImg = styled.div<{ $color: string }>`
  flex: 0 0 36%;
  align-self: stretch;
  border-radius: 2px;
  background: ${(p) => p.$color};
`;

export const WireLines = styled.div`
  flex: 1 1 0;
  min-width: 0;
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 3px;
`;

export const WireBar = styled.div<{ $color: string; $w?: string; $h?: string }>`
  width: ${(p) => p.$w ?? '92%'};
  height: ${(p) => p.$h ?? '3px'};
  border-radius: 1px;
  background: ${(p) => p.$color};
`;

export const WireBtn = styled.div<{ $color: string }>`
  margin-top: auto;
  width: 52%;
  height: 8px;
  border-radius: 3px;
  background: ${(p) => p.$color};
`;

// --- Top Websites ------------------------------------------------------------
export const WebsitesSection = styled.section`
  display: block;
`;

export const WebsitesPanel = styled.div`
  ${listingSurface}
  padding: 4px;
`;

/** Placeholder shown when the campaign has driven no publisher traffic yet (the API
 *  returns no top sites) — keeps the section visibly wired rather than hidden. */
export const WebsitesEmpty = styled(Text)`
  display: block;
  padding: 22px 12px;
  text-align: center;
  font-size: 13px;
  font-weight: 300;
  color: ${COLORS.textMuted};
`;

/**
 * Clips the stacked rows to a rounded rectangle that is concentric with the
 * panel: the outer 16px radius minus the 1px border and 4px padding gives an
 * 11px inner radius. This masks each row's square hover wash so the first row's
 * top corners and the last row's bottom corners follow the container's curve
 * exactly instead of poking into it.
 */
export const WebsitesList = styled.div`
  clip-path: inset(0 round 11px);
`;

export const Rank = styled(Text)`
  flex: 0 0 auto;
  min-width: 18px;
  font-size: 14px;
  font-weight: 600;
  color: ${COLORS.textFaint};
  font-variant-numeric: tabular-nums;
`;

/**
 * Name + visits + external-link icon, grouped on one line directly after the
 * rank (not spread to the row's far edge), with a bullet between the name and
 * the metric. `flex: 1` lets it own the rest of the row so the whole width stays
 * a clickable tap target.
 */
export const RowLine = styled.div`
  flex: 1 1 auto;
  min-width: 0;
  display: flex;
  align-items: center;
  gap: 8px;
`;

export const WebsiteName = styled(Text)`
  flex: 0 1 auto;
  min-width: 0;
  display: block;
  font-size: 14px;
  font-weight: 500;
  color: inherit;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

export const Bullet = styled.span`
  flex: 0 0 auto;
  font-size: 14px;
  line-height: 1;
  color: ${COLORS.textFaint};

  &::before {
    content: '•';
  }
`;

export const WebsiteValue = styled(Text)`
  flex: 0 0 auto;
  font-size: 14px;
  font-weight: 400;
  color: ${COLORS.textMuted};
  white-space: nowrap;
`;

/**
 * One website ranking row. The whole row is the outbound link, kept at least
 * 48px tall so it stays a comfortable mobile tap target. Adjacent rows are
 * divided by a hairline rule, and the row lifts with a faint wash (and the name
 * underlines) on hover / keyboard focus.
 */
export const WebsiteRow = styled.a`
  display: flex;
  align-items: center;
  gap: 12px;
  min-height: 48px;
  padding: 0 12px;
  text-decoration: none;
  color: ${COLORS.textPale};
  transition:
    background ${ANIM.INTERACT_MS}ms ease,
    color ${ANIM.INTERACT_MS}ms ease;

  & + & {
    border-top: 1px solid ${COLORS.hairline};
  }

  &:hover,
  &:focus-visible {
    background: ${COLORS.hoverWash};
    color: ${COLORS.text};
  }

  &:hover ${WebsiteName}, &:focus-visible ${WebsiteName} {
    text-decoration: underline;
  }
`;
